import React, { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * ErrorBoundary - Captura erros JavaScript e exibe página amigável
 * Mantém o estilo visual do Oráculo
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div id="oraculo-root">
                    <div className="background-container">
                        <div className="background-overlay" />
                    </div>
                    <div className="main-container">
                        <div className="error-page">
                            <div className="error-icon">🔮</div>
                            <h1 className="error-title">As névoas obscurecem o caminho...</h1>
                            <p className="error-message">
                                Um distúrbio nas forças místicas impediu o Oráculo de responder.
                                <br />
                                Por favor, tente novamente.
                            </p>
                            <button className="error-retry-button" onClick={this.handleRetry}>
                                ↻ Invocar novamente
                            </button>
                            <p className="error-support">
                                Se o problema persistir, entre em contato com o{' '}
                                <a
                                    href="https://wa.me/5516997618739?text=Ol%C3%A1%2C%20vim%20do%20Or%C3%A1culo%20e%20preciso%20de%20uma%20ajudinha%20humana%20%F0%9F%98%84"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    suporte humano
                                </a>
                                .
                            </p>
                        </div>
                    </div>
                    <style>{`
                        .error-page {
                            text-align: center;
                            max-width: 500px;
                            padding: 2rem;
                            background: rgba(15, 15, 20, 0.85);
                            backdrop-filter: blur(30px);
                            border: 1px solid rgba(255, 255, 255, 0.12);
                            border-radius: 24px;
                            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
                        }
                        .error-icon {
                            font-size: 4rem;
                            margin-bottom: 1rem;
                            filter: grayscale(0.5);
                        }
                        .error-title {
                            font-size: 1.5rem;
                            font-weight: 600;
                            color: #ffffff;
                            margin-bottom: 1rem;
                        }
                        .error-message {
                            color: rgba(255, 255, 255, 0.7);
                            font-size: 1rem;
                            line-height: 1.6;
                            margin-bottom: 1.5rem;
                        }
                        .error-retry-button {
                            background: rgba(255, 255, 255, 0.9);
                            color: #0a0a0a;
                            border: none;
                            border-radius: 50px;
                            padding: 0.875rem 2rem;
                            font-size: 1rem;
                            font-weight: 600;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            margin-bottom: 1.5rem;
                        }
                        .error-retry-button:hover {
                            background: #ffffff;
                            transform: scale(1.05);
                            box-shadow: 0 4px 16px rgba(255, 255, 255, 0.3);
                        }
                        .error-support {
                            font-size: 0.875rem;
                            color: rgba(255, 255, 255, 0.5);
                        }
                        .error-support a {
                            color: #8ab4f8;
                            text-decoration: none;
                        }
                        .error-support a:hover {
                            text-decoration: underline;
                        }
                    `}</style>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
